import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { useInitData } from '../../../hooks'
import { useRequestProfileContext } from '../RequestProfile.hooks'

import { REQUEST_PROFILE_HEADER_FOOTER_QA_NAME } from './RequestProfileHeaderFooter.constants'
import {
  useRequestProfileHeaderFooterActions,
  useRequestProfileHeaderFooterMeasure,
} from './RequestProfileHeaderFooter.hooks'
import {
  getRequestProfileHeaderFooterActions,
  getRequestProfileHeaderFooterMoreOptions,
} from './RequestProfileHeaderFooter.utils'
import { RequestProfileHeaderFooterActionButton } from './RequestProfileHeaderFooterActionButton'
import { RequestProfileHeaderFooterAddNewText } from './RequestProfileHeaderFooterAddNewText'
import { RequestProfileHeaderFooterHiddenMeasureLayer } from './RequestProfileHeaderFooterHiddenMeasureLayer'
import { RequestProfileHeaderFooterMoreButton } from './RequestProfileHeaderFooterMoreButton'
import { RequestProfileHeaderFooterSkeleton } from './RequestProfileHeaderFooterSkeleton'

import * as styles from './RequestProfileHeaderFooter.module.css'

function RequestProfileHeaderFooter() {
  const { isLoading, requestData } = useRequestProfileContext()
  const { t } = useTranslation()

  const { sections } = useInitData()

  const { actionHandlers, getUploadFileInputProps, selectHandlers } =
    useRequestProfileHeaderFooterActions()

  const actions = getRequestProfileHeaderFooterActions({
    requestData,
    sections,
    t,
  })

  const {
    addNewTextMeasureRef,
    addNewTextRef,
    getActionButtonRef,
    handleContainerRef,
    hiddenActions,
    moreButtonMeasureRef,
    visibleActions,
  } = useRequestProfileHeaderFooterMeasure({ actions })

  const hasActionButtons = actions.length > 0
  const hasMoreButton = hiddenActions.length > 0
  const isOnlyMoreWithAddNewText = hasMoreButton && visibleActions.length < 1

  const moreOptions = getRequestProfileHeaderFooterMoreOptions(hiddenActions)

  function handleSelectMoreOption({ value }) {
    actionHandlers[value]?.()
  }

  return isLoading ? (
    <RequestProfileHeaderFooterSkeleton elementRef={handleContainerRef} />
  ) : hasActionButtons ? (
    <>
      <div className={styles.footer}>
        <div className={styles.container} ref={handleContainerRef}>
          <div className={styles.bar}>
            <div
              className={classNames(styles.addNewTextWrapper, {
                [styles.compactEdgeButtonWrapper]: isOnlyMoreWithAddNewText,
              })}
              ref={addNewTextRef}
            >
              <RequestProfileHeaderFooterAddNewText
                qaName={REQUEST_PROFILE_HEADER_FOOTER_QA_NAME.addNew}
              />
            </div>

            {visibleActions.map(action => (
              <div
                className={styles.actionButtonWrapper}
                key={action.action}
                ref={getActionButtonRef(action.action)}
              >
                <RequestProfileHeaderFooterActionButton
                  {...action}
                  onClick={actionHandlers[action.action]}
                  onSelectOption={selectHandlers[action.action]}
                />
              </div>
            ))}

            {hiddenActions.length > 0 ? (
              <RequestProfileHeaderFooterMoreButton
                buttonClassName={classNames({
                  [styles.compactEdgeButton]: isOnlyMoreWithAddNewText,
                })}
                className={classNames(styles.moreButton, {
                  [styles.compactEdgeButtonWrapper]: isOnlyMoreWithAddNewText,
                })}
                options={moreOptions}
                qaName={REQUEST_PROFILE_HEADER_FOOTER_QA_NAME.more}
                onSelectItem={handleSelectMoreOption}
              />
            ) : null}
          </div>

          <RequestProfileHeaderFooterHiddenMeasureLayer
            addNewTextMeasureRef={addNewTextMeasureRef}
            moreButtonMeasureRef={moreButtonMeasureRef}
          />
        </div>
      </div>

      <input {...getUploadFileInputProps()} />
    </>
  ) : null
}

export default RequestProfileHeaderFooter
